import { Controller } from "@hotwired/stimulus"

export const PX_PER_MINUTE = 0.8 // 48px per hour — keep in sync with _weekly_hours.html.erb
export const SNAP = 30
export const MIN_BLOCK = 30
const DAY_MINUTES = 24 * 60

// --- pure geometry helpers ---
export function hhmmToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number)
  return h * 60 + m
}
export function minutesToHHMM(min) {
  const h = String(Math.floor(min / 60)).padStart(2, "0")
  const m = String(min % 60).padStart(2, "0")
  return `${h}:${m}`
}
export function minutesToLabel(min) {
  const h24 = Math.floor(min / 60)
  const m = String(min % 60).padStart(2, "0")
  const period = h24 < 12 ? "AM" : "PM"
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${m} ${period}`
}
export function snapTo(minutes, snap = SNAP) {
  return Math.min(DAY_MINUTES, Math.max(0, Math.round(minutes / snap) * snap))
}
// Free window around `anchor` given sorted neighbor ranges [{start,end}]
export function freeWindow(anchor, neighbors) {
  let lo = 0
  let hi = DAY_MINUTES
  for (const n of neighbors) {
    if (n.end <= anchor) lo = Math.max(lo, n.end)
    if (n.start >= anchor) hi = Math.min(hi, n.start)
  }
  return [lo, hi]
}

export default class extends Controller {
  static targets = ["column", "block", "templateInput", "saveBar", "scroll", "dayTab",
    "copyToggle", "copyPanel", "copyNote", "copyDayCheckbox"]

  connect() {
    // Open the working day: scroll to 7 AM
    if (this.hasScrollTarget) this.scrollTarget.scrollTop = 7 * 60 * PX_PER_MINUTE
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
    this._activateDay(days[new Date().getDay()])
  }

  // ----- form -----
  serialize() {
    const template = {}
    this.columnTargets.forEach((col) => {
      const blocks = [...col.querySelectorAll("[data-availability-grid-target='block']")]
        .map((el) => ({ start: el.dataset.start, end: el.dataset.end }))
        .sort((a, b) => hhmmToMinutes(a.start) - hhmmToMinutes(b.start))
      template[col.dataset.day] = blocks
    })
    this.templateInputTarget.value = JSON.stringify(template)
  }

  discard() {
    window.location.reload()
  }

  markDirty() {
    this.saveBarTarget.classList.remove("hidden")
  }

  // ----- block management -----
  removeBlock(event) {
    event.stopPropagation()
    event.target.closest("[data-availability-grid-target='block']").remove()
    this.markDirty()
  }

  // ----- helpers -----
  _ranges(col, except = null) {
    return [...col.querySelectorAll("[data-availability-grid-target='block']")]
      .filter((el) => el !== except && !el.classList.contains("availability-grid__block--ghost"))
      .map((el) => ({ start: hhmmToMinutes(el.dataset.start), end: hhmmToMinutes(el.dataset.end) }))
      .sort((a, b) => a.start - b.start)
  }

  _createBlock(col, startMin, endMin) {
    const el = document.createElement("div")
    el.className = "availability-grid__block"
    el.tabIndex = 0
    el.setAttribute("role", "group")
    el.setAttribute("data-availability-grid-target", "block")
    el.setAttribute("data-action",
      "pointerdown->availability-grid#beginBlockDrag keydown->availability-grid#keydown")

    const topHandle = document.createElement("div")
    topHandle.className = "availability-grid__handle availability-grid__handle--top"
    topHandle.setAttribute("data-handle", "top")

    const label = document.createElement("span")
    label.className = "availability-grid__block-label"

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "availability-grid__block-remove"
    remove.setAttribute("data-action", "availability-grid#removeBlock")
    remove.setAttribute("aria-label", "Remove block")
    remove.innerHTML = '<i class="fa-solid fa-xmark text-[10px]" aria-hidden="true"></i>'

    const bottomHandle = document.createElement("div")
    bottomHandle.className = "availability-grid__handle availability-grid__handle--bottom"
    bottomHandle.setAttribute("data-handle", "bottom")

    el.append(topHandle, label, remove, bottomHandle)
    col.appendChild(el)
    this._updateBlock(el, startMin, endMin)
    return el
  }

  _updateBlock(el, startMin, endMin) {
    el.dataset.start = minutesToHHMM(startMin)
    el.dataset.end = minutesToHHMM(endMin)
    el.style.top = `${startMin * PX_PER_MINUTE}px`
    el.style.height = `${(endMin - startMin) * PX_PER_MINUTE}px`
    const text = `${minutesToLabel(startMin)} – ${minutesToLabel(endMin)}`
    el.querySelector(".availability-grid__block-label").textContent = text
    const day = el.closest("[data-day]").dataset.day
    el.setAttribute("aria-label", `${day.charAt(0).toUpperCase()}${day.slice(1)} ${text}`)
  }

  // ----- pointer: draw on empty space -----
  beginDraw(event) {
    if (event.target !== event.currentTarget) return // block/handle clicks handled elsewhere
    if (event.button !== 0 && event.pointerType === "mouse") return
    event.preventDefault()
    const col = event.currentTarget
    const anchor = snapTo(this._eventMinutes(event, col))
    const [lo, hi] = freeWindow(anchor, this._ranges(col))
    if (hi - lo < MIN_BLOCK) return
    const growDown = hi - anchor >= SNAP
    const initial = growDown ? [anchor, anchor + SNAP] : [anchor - SNAP, anchor]
    const el = this._createBlock(col, initial[0], initial[1])
    el.classList.add("availability-grid__block--ghost")
    this._drag = { mode: "draw", el, col, anchor, lo, hi, growDown }
    this._bindWindow()
  }

  // ----- pointer: move or resize an existing block -----
  beginBlockDrag(event) {
    if (event.button !== 0 && event.pointerType === "mouse") return
    if (event.target.closest(".availability-grid__block-remove")) return
    event.preventDefault()
    event.stopPropagation()
    const el = event.currentTarget
    const col = el.closest("[data-availability-grid-target='column']")
    const start = hhmmToMinutes(el.dataset.start)
    const end = hhmmToMinutes(el.dataset.end)
    const [lo, hi] = freeWindow(start, this._ranges(col, el))
    const handle = event.target.closest("[data-handle]")?.dataset.handle
    const mode = handle === "top" ? "resize-top" : handle === "bottom" ? "resize-bottom" : "move"
    const offset = this._eventMinutes(event, col) - start
    this._drag = { mode, el, col, lo, hi, length: end - start, offset }
    el.classList.add("availability-grid__block--ghost")
    this._bindWindow()
  }

  _onPointerMove = (event) => {
    if (!this._drag) return
    const { mode, el, col, anchor, lo, hi, length, offset, growDown } = this._drag
    const cursor = this._eventMinutes(event, col)

    if (mode === "draw") {
      const a = Math.max(lo, Math.min(snapTo(cursor), hi))
      if (a > anchor || (a === anchor && growDown)) {
        this._updateBlock(el, anchor, Math.max(a, Math.min(anchor + SNAP, hi)))
      } else {
        this._updateBlock(el, Math.min(a, Math.max(anchor - SNAP, lo)), anchor)
      }
    } else if (mode === "move") {
      let start = snapTo(cursor - offset)
      start = Math.max(lo, Math.min(start, hi - length))
      this._updateBlock(el, start, start + length)
    } else if (mode === "resize-top") {
      const end = hhmmToMinutes(el.dataset.end)
      const start = Math.max(lo, Math.min(snapTo(cursor), end - MIN_BLOCK))
      this._updateBlock(el, start, end)
    } else if (mode === "resize-bottom") {
      const start = hhmmToMinutes(el.dataset.start)
      const end = Math.min(hi, Math.max(snapTo(cursor), start + MIN_BLOCK))
      this._updateBlock(el, start, end)
    }
  }

  _onPointerUp = () => {
    if (!this._drag) return
    const { mode, el, lo, hi } = this._drag
    el.classList.remove("availability-grid__block--ghost")
    if (mode === "draw") {
      const start = hhmmToMinutes(el.dataset.start)
      const end = hhmmToMinutes(el.dataset.end)
      if (end - start < MIN_BLOCK) {
        el.remove()
      } else if (end - start === SNAP) {
        // Bare click: grow to one hour where room allows
        const grownEnd = Math.min(start + 60, hi)
        const grownStart = Math.max(grownEnd - 60, lo)
        this._updateBlock(el, grownStart, grownEnd)
      }
    }
    this._drag = null
    this._unbindWindow()
    this.markDirty()
  }

  _eventMinutes(event, col) {
    const rect = col.getBoundingClientRect()
    return (event.clientY - rect.top) / PX_PER_MINUTE
  }

  _bindWindow() {
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp, { once: true })
  }

  _unbindWindow() {
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
  }

  disconnect() {
    this._unbindWindow()
    this._drag = null
  }

  keydown(event) {
    const el = event.currentTarget
    const col = el.closest("[data-availability-grid-target='column']")
    const start = hhmmToMinutes(el.dataset.start)
    const end = hhmmToMinutes(el.dataset.end)
    const [lo, hi] = freeWindow(start, this._ranges(col, el))

    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault()
      el.remove()
      this.markDirty()
      return
    }
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return
    event.preventDefault()
    const delta = event.key === "ArrowUp" ? -SNAP : SNAP

    if (event.shiftKey) {
      const newEnd = Math.min(hi, Math.max(start + MIN_BLOCK, end + delta))
      this._updateBlock(el, start, newEnd)
    } else {
      const length = end - start
      const newStart = Math.max(lo, Math.min(start + delta, hi - length))
      this._updateBlock(el, newStart, newStart + length)
    }
    this.markDirty()
  }

  selectDay(event) {
    this._activateDay(event.currentTarget.dataset.day)
  }

  _activateDay(day) {
    this._activeDay = day
    // The copy panel's note and checkbox states describe the day it was
    // opened on — close it so it can't drift out of sync with the new day.
    this._closeCopyPanel()
    this.columnTargets.forEach((col) =>
      col.classList.toggle("availability-grid__col--active", col.dataset.day === day))
    if (!this.hasDayTabTarget) return
    this.dayTabTargets.forEach((tab) => {
      const active = tab.dataset.day === day
      tab.classList.toggle("bg-primary-600", active)
      tab.classList.toggle("text-white", active)
      tab.setAttribute("aria-selected", active ? "true" : "false")
    })
  }

  // ----- copy-day (day-tab mode) -----
  toggleCopyPanel() {
    const hidden = this.copyPanelTarget.classList.toggle("hidden")
    this.copyToggleTarget.setAttribute("aria-expanded", hidden ? "false" : "true")
    if (hidden) return

    const sourceEmpty = this._activeRanges().length === 0
    this.copyNoteTarget.textContent = sourceEmpty
      ? "This day has no hours — the selected days will be cleared."
      : "Replaces the selected days' hours."
    this.copyDayCheckboxTargets.forEach((box) => {
      const isActive = box.value === this._activeDay
      box.checked = false
      box.disabled = isActive
      box.closest("label").classList.toggle("opacity-40", isActive)
    })
  }

  closeCopyPanel() {
    this._closeCopyPanel()
  }

  applyCopyDays() {
    const checked = this.copyDayCheckboxTargets.filter((box) => box.checked && !box.disabled)
    this._closeCopyPanel()
    if (checked.length === 0) return

    const blocks = this._activeRanges()
    checked.forEach((box) => {
      const col = this.columnTargets.find((c) => c.dataset.day === box.value)
      col.querySelectorAll("[data-availability-grid-target='block']").forEach((el) => el.remove())
      blocks.forEach(({ start, end }) => this._createBlock(col, start, end))
    })
    this.markDirty()
  }

  _closeCopyPanel() {
    if (!this.hasCopyPanelTarget) return
    this.copyPanelTarget.classList.add("hidden")
    this.copyToggleTarget.setAttribute("aria-expanded", "false")
  }

  _activeRanges() {
    const col = this.columnTargets.find((c) => c.dataset.day === this._activeDay)
    return col ? this._ranges(col) : []
  }
}
